import _ from "lodash";
import {
    DASHBOARD,
    PAGE_LAYOUTS_CREATE,
    PAGE_LAYOUTS_DELETE,
    PAGE_LAYOUTS_UPDATE,
    PAGE_LAYOUTS_UPDATE_COMPLETED,
    PAGE_LAYOUTS_PAGE_CREATE,
    PAGE_LAYOUTS_COPY,
} from "constants/action-types";
import { push } from "react-router-redux";
import {
    createPageLayout,
    createPageLayoutError,
    createPageLayoutSuccess,
    deletePageLayoutError,
    deletePageLayoutSuccess,
    copyPageLayoutError,
    copyPageLayoutSuccess,
    updatePageLayoutSuccess,
} from "actions/dashboard/page-layouts";
import { add as snackbarAdd } from "actions/snackbars";
import { formatMessage } from "lib/globalize-util";
import { PageLayoutsSchema } from "schemas/pages";
import { BrandProfiles as BrandProfilesSchema } from "schemas/brand";
import { v4 } from "uuid";

/**
 * Segment Stuff
 */

import { EventTypes } from "redux-segment";

/**
 * redux-query stuff
 */

import { getRecordsFromSchema, normalizeData } from "lib/api";
import { updateEntities } from "redux-query";

/**
 * Observable stuff
 */

import { ajax } from "lib/observable";
import { Observable } from "rxjs";
import "rxjs/add/operator/map";
import { APIError } from "lib/errors";
import siteConfig from "site-config";

export const updatePageLayoutEpic = (action$, store) =>
    action$
        .ofType(PAGE_LAYOUTS_UPDATE)
        .map(({ payload }) => payload)
        .mergeMap(({ name, description, pageId, layoutId }) => {
            const request = {
                body: {
                    page_id: pageId,
                    page_layout: { name, description },
                    page_layout_id: layoutId,
                    update_mask: {
                        paths: Object.keys({ name, description }),
                    },
                },
                method: "PATCH",
                url: `${siteConfig.goAPI}/v1/pages/${pageId}/layouts/${layoutId}`,
            };
            return ajax(request)
                .map(({ response }) => {
                    if (!response) {
                        return { result: [] };
                    } else {
                        const records = getRecordsFromSchema(PageLayoutsSchema);
                        const data = normalizeData(response, PageLayoutsSchema, records);
                        return { entities: data.entities, result: data.result };
                    }
                })
                .mergeMap(({ entities, result }: any) => [
                    updateEntities({
                        pageLayouts: (pageLayouts = {}) => ({
                            ...pageLayouts,
                            ...entities.pageLayouts,
                        }),
                    }),
                    updatePageLayoutSuccess({}),
                    store.dispatch(
                        snackbarAdd({
                            message: formatMessage("Page updated"),
                        }),
                    ),
                ])
                .catch((error) =>
                    Observable.of({
                        error: true,
                        payload: new APIError(error.xhr.response.error, error.xhr.status, error.xhr.statusText),
                        type: PAGE_LAYOUTS_UPDATE_COMPLETED,
                    }),
                );
        });

export const createPageEpic = (action$, store) =>
    action$
        .ofType(PAGE_LAYOUTS_PAGE_CREATE)
        .map(({ payload }) => payload)
        .mergeMap(({ name, description }) => {
            const { brand } = store.getState().scope;
            const request = {
                body: {
                    brand_id: brand._id,
                    name: brand.name,
                    published: false,
                },
                method: "POST",
                url: `${siteConfig.goAPI}/v1/pages`,
            };

            return ajax(request)
                .map(({ response }) => {
                    if (!response) {
                        return { entities: { brandProfile: {} }, result: [] };
                    } else {
                        const records = getRecordsFromSchema(BrandProfilesSchema);
                        const data = normalizeData(response, PageLayoutsSchema, records);
                        return { entities: data.entities, result: data.result };
                    }
                })
                .mergeMap(({ entities, result }: any) => [
                    updateEntities({
                        brandProfile: (brandProfile = {}) => ({
                            ...brandProfile,
                            ...entities.brandProfile,
                        }),
                    }),
                    createPageLayout({ name, description, pageId: result[0] }),
                ])
                .catch((error) =>
                    Observable.of(
                        snackbarAdd({
                            message: formatMessage("There was an error creating the page"),
                        }),
                    ),
                );
        });

export const createPageLayoutEpic = (action$, store) =>
    action$
        .ofType(PAGE_LAYOUTS_CREATE)
        .map(({ payload }) => payload)
        .mergeMap(({ name, description, pageId: page_id }) => {
            const { scope } = store.getState();
            const request = {
                body: { name, description, page_id },
                method: "POST",
                url: `${siteConfig.goAPI}/v1/pages/${page_id}/layouts`,
            };

            return ajax(request)
                .map(({ response }) => {
                    if (!response) {
                        return { entities: { pageLayouts: {} }, result: [] };
                    } else {
                        const records = getRecordsFromSchema(PageLayoutsSchema);
                        const data = normalizeData(response, PageLayoutsSchema, records);
                        return { entities: data.entities, result: data.result };
                    }
                })
                .mergeMap(({ entities, result }: any) => [
                    updateEntities({
                        pageLayouts: (pageLayouts = {}) => ({
                            ...pageLayouts,
                            ...entities.pageLayouts,
                        }),
                    }),
                    createPageLayoutSuccess(result[0], {
                        analytics: {
                            eventPayload: {
                                event: "Page created",
                                options: {
                                    groupId: scope.brand._id,
                                },
                                properties: {
                                    brand: scope.brand._id,
                                    category: "Wholesale",
                                },
                            },
                            eventType: EventTypes.track,
                        },
                    }),
                    push(`/brand/${scope.brand._id}/page/${result[0]}?edit=true`),
                    store.dispatch(
                        snackbarAdd({
                            message: formatMessage("Page successfully created"),
                        }),
                    ),
                ])
                .catch((error) =>
                    Observable.merge(
                        Observable.of(
                            createPageLayoutError(
                                new APIError(error.xhr.response.error, error.xhr.status, error.xhr.statusText),
                            ),
                        ),
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error creating the page"),
                            }),
                        ),
                    ),
                );
        });
// prettier-ignore
export const deletePageLayoutEpic = (action$, store) =>
    action$
        .ofType(PAGE_LAYOUTS_DELETE)
        .map(({ payload }) => payload)
        .mergeMap(({ pageId, layoutId }) =>
            ajax({
                url: `${siteConfig.goAPI}/v1/pages/${pageId}/layouts/${layoutId}`,
                method: "DELETE",
            })
                .mergeMap(() => {
                    const { routing, scope } = store.getState();

                    return routing.locationBeforeTransitions.pathname.includes("/pages")
                        ? [
                            deletePageLayoutSuccess(layoutId),
                            store.dispatch(
                                snackbarAdd({
                                    message: formatMessage("Page successfully deleted"),
                                })
                            ),
                        ]
                        : [
                            deletePageLayoutSuccess(layoutId),
                            push(`/brand/${scope.brand._id}/pages`),
                            store.dispatch(
                                snackbarAdd({
                                    message: formatMessage("Page successfully deleted"),
                                })
                            ),
                        ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of(deletePageLayoutError(new APIError(error.xhr.response.error, error.xhr.status, error.xhr.statusText))),
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error deleting the page"),
                            })
                        )
                    )
                )
        );

export const copyPageLayoutEpic = (action$, store) =>
    // TODO: Copy request payload
    action$
        .ofType(PAGE_LAYOUTS_COPY)
        .map(({ payload }) => payload)
        .mergeMap(({ name, description, layoutId, pageId }) => {
            const request = {
                body: {},
                method: "POST",
                url: `${siteConfig.goAPI}/v1/pages/${pageId}/layouts/${layoutId}/copy`,
            };

            return ajax(request)
                .map(({ response }) => {
                    if (!response) {
                        return { copiedEntities: { pageLayouts: {} } };
                    } else {
                        const records = getRecordsFromSchema(PageLayoutsSchema);
                        const data = normalizeData(response, PageLayoutsSchema, records);
                        return { copiedEntities: data.entities, components: response.data[0]?.components };
                    }
                })
                .mergeMap(({ copiedEntities, components }) => {
                    const newPageLayout = _.chain(copiedEntities.pageLayouts).values().first().value();
                    const page_id = _.get(newPageLayout, "page_id", "");
                    const page_layout_id = _.get(newPageLayout, "id", "");
                    _.each(components, (component) => {
                        if (component?.display_properties?.componentUuid) {
                            component.ref_id = v4();
                            component.display_properties.componentUuid = component.ref_id;
                        }
                    });

                    const pageLayout = { name, description, components };
                    const request = {
                        body: {
                            page_id,
                            page_layout: pageLayout,
                            page_layout_id,
                            update_mask: {
                                paths: _.keys(pageLayout),
                            },
                        },
                        method: "PATCH",
                        url: `${siteConfig.goAPI}/v1/pages/${page_id}/layouts/${page_layout_id}`,
                    };
                    return ajax(request)
                        .map(({ response }) => {
                            if (!response) {
                                return { entities: { pageLayouts: {} }, result: [] };
                            } else {
                                const records = getRecordsFromSchema(PageLayoutsSchema);
                                const data = normalizeData(response, PageLayoutsSchema, records);
                                return { entities: data.entities, result: data.result };
                            }
                        })
                        .mergeMap(({ entities, result }) => {
                            const { routing, scope } = store.getState();
                            const actionsQueue = [
                                updateEntities({
                                    pageLayouts: (pageLayouts = {}) => ({
                                        ...pageLayouts,
                                        ...entities.pageLayouts,
                                    }),
                                }),
                                copyPageLayoutSuccess(result[0]),
                                store.dispatch(
                                    snackbarAdd({
                                        message: formatMessage("Page successfully copied"),
                                    }),
                                ),
                            ];

                            if (_.get(routing, ["locationBeforeTransitions", "query", "edit"]) === "true") {
                                actionsQueue.push(push(`/brand/${scope.brand._id}/page/${result[0]}/?edit=true`));
                            }

                            return actionsQueue;
                        });
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of(
                            copyPageLayoutError(
                                new APIError(error.xhr.response.error, error.xhr.status, error.xhr.statusText),
                            ),
                        ),
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error copying the page"),
                            }),
                        ),
                    ),
                );
        });

export const dashboardMutateEpic = (action$, store) =>
    action$.ofType(DASHBOARD.MUTATE).mergeMap(({ error, payload = { message: "" } }) => {
        if (error) {
            let message = formatMessage("There was an error updating the page");
            if (payload.message) {
                message += `: ${payload.message}`;
            }
            return Observable.of(
                snackbarAdd({
                    message,
                }),
            );
        }

        return Observable.of(payload);
    });
